import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { ButtonLink } from './shared/ButtonLink';
import type {
  NavigationMenuComponent as NavigationMenuComponentType,
  NavigationMenuLink,
} from '../schemas/validation';
import { usePreviewContext } from '../contexts/PreviewContext';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { clampLogoImageSizeForRender } from './LogoUploadModal/useLogoUpload';
import { isLegacyBothLogo } from '../utils/navbarLogo';
import { previewNavbarUrl, resolveNavbarLinkUrl, uniqueNavbarLinks } from '../utils/navbarLinks';
import { visibleMenuLinkText } from '../utils/menuLinkLabel';

interface NavigationMenuComponentProps {
  component: NavigationMenuComponentType;
}

const isHexColor = (color: string | undefined) => !!color?.startsWith('#');

export const NavigationMenuComponent: React.FC<NavigationMenuComponentProps> = ({ component }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [openParents, setOpenParents] = React.useState<Record<string, boolean>>({});
  const { data } = component;
  const { isPreviewMode, siteId } = usePreviewContext();
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const hasMenuBrand =
    !!customColors?.navigationMenu && Object.keys(customColors.navigationMenu).length > 0;
  const hasNavbarBrand = !!customColors?.navbar && Object.keys(customColors.navbar).length > 0;
  const menuStyles = themeStyles.navigationMenu;
  const navbarStyles = themeStyles.navbar;
  const navTheme =
    !hasMenuBrand && hasNavbarBrand
      ? {
          background: navbarStyles?.background,
          text: navbarStyles?.text,
          brandText: navbarStyles?.brandText,
          linkText: navbarStyles?.linkText,
          linkHover: navbarStyles?.linkHover,
          border: navbarStyles?.border,
          shadow: navbarStyles?.shadow,
          height: navbarStyles?.height,
          buttonBg: navbarStyles?.buttonBg,
          buttonText: navbarStyles?.buttonText,
          buttonHoverBg: navbarStyles?.buttonHoverBg,
          dropdownBackground: navbarStyles?.background || '#ffffff',
          dropdownText: navbarStyles?.linkText || navbarStyles?.text,
          dropdownHover: navbarStyles?.linkHover,
        }
      : {
          background: menuStyles?.background || navbarStyles?.background,
          text: menuStyles?.text || navbarStyles?.text,
          brandText: menuStyles?.brandText || navbarStyles?.brandText,
          linkText: menuStyles?.linkText || navbarStyles?.linkText,
          linkHover: menuStyles?.linkHover || navbarStyles?.linkHover,
          border: menuStyles?.border || navbarStyles?.border,
          shadow: menuStyles?.shadow || navbarStyles?.shadow,
          height: menuStyles?.height || navbarStyles?.height,
          buttonBg: menuStyles?.buttonBg || navbarStyles?.buttonBg,
          buttonText: menuStyles?.buttonText || navbarStyles?.buttonText,
          buttonHoverBg: menuStyles?.buttonHoverBg || navbarStyles?.buttonHoverBg,
          dropdownBackground: menuStyles?.dropdownBackground || '#ffffff',
          dropdownText: menuStyles?.dropdownText || menuStyles?.linkText || navbarStyles?.linkText,
          dropdownHover:
            menuStyles?.dropdownHover || menuStyles?.linkHover || navbarStyles?.linkHover,
        };

  const getNavigationUrl = (url: string) => previewNavbarUrl(url, { isPreviewMode, siteId });

  const linkAlignmentClasses = {
    left: 'ml-10',
    center: 'mx-auto',
    right: 'ml-auto',
    'space-between': 'ml-auto',
  };

  const navbarStyle: React.CSSProperties = {};
  const navbarClasses: string[] = ['overflow-visible', 'transition-all duration-200'];
  if (data.sticky !== false) navbarClasses.push('z-50');

  const backgroundColor =
    data.customColors?.background || navTheme.background || data.backgroundColor;
  if (backgroundColor && isHexColor(backgroundColor)) {
    navbarStyle.backgroundColor = backgroundColor;
  } else if (backgroundColor) {
    navbarClasses.push(backgroundColor);
  }

  const textColor = data.customColors?.text || navTheme.text || data.textColor;
  if (textColor) {
    if (isHexColor(textColor)) navbarStyle.color = textColor;
    else navbarClasses.push(textColor);
  }

  if (navTheme.shadow && !isHexColor(navTheme.shadow)) navbarClasses.push(navTheme.shadow);
  if (navTheme.border && !isHexColor(navTheme.border)) navbarClasses.push(navTheme.border);
  if (navTheme.height) navbarClasses.push(navTheme.height);

  const sortedLinks = uniqueNavbarLinks([...(data.links || [])].sort((a, b) => a.order - b.order));
  const navLinkHoverColor = data.customColors?.linkHover || navTheme.linkHover || '';
  const dropdownBackground = data.customColors?.dropdownBackground || navTheme.dropdownBackground;
  const dropdownText = data.customColors?.dropdownText || navTheme.dropdownText;
  const dropdownHover = data.customColors?.dropdownHover || navTheme.dropdownHover || '';

  const getLinkStyles = (linkStyle: string = 'normal', isMobile: boolean = false) => {
    const linkColor = data.customColors?.linkText || navTheme.linkText || navTheme.text;
    const buttonBg = navTheme.buttonBg || '#3b82f6';
    const buttonText = navTheme.buttonText || '#ffffff';
    const buttonHoverBg = navTheme.buttonHoverBg || '#2563eb';
    const baseStyle: React.CSSProperties = {};
    let baseClass = '';

    switch (linkStyle) {
      case 'button':
        baseClass = isMobile
          ? 'block w-full text-center px-4 py-3 rounded-lg text-base font-semibold transition-all'
          : 'inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all';
        if (isHexColor(buttonBg)) baseStyle.backgroundColor = buttonBg;
        else baseClass += ` ${buttonBg || 'bg-blue-600'}`;
        if (isHexColor(buttonText)) baseStyle.color = buttonText;
        else baseClass += ` ${buttonText || 'text-white'}`;
        baseClass += ' hover:opacity-90';
        return { style: baseStyle, className: baseClass, isButton: true, buttonHoverBg };
      case 'outline':
        baseClass = isMobile
          ? 'nav-hover-link block w-full text-center px-4 py-3 rounded-lg text-base font-semibold transition-all border-2'
          : 'nav-hover-link inline-flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-semibold transition-all border-2';
        if (isHexColor(linkColor)) {
          baseStyle.color = linkColor;
          baseStyle.borderColor = linkColor;
        } else {
          baseClass += ` ${linkColor || 'text-gray-700'} border-current`;
        }
        baseClass += ' hover:opacity-80 bg-transparent';
        return {
          style: baseStyle,
          className: baseClass,
          isButton: false,
          linkColor,
          linkHover: navLinkHoverColor,
        };
      default:
        baseClass = isMobile
          ? 'nav-hover-link flex w-full items-center justify-between px-3 py-2 rounded-md text-base font-medium transition-all'
          : 'nav-hover-link inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-all';
        if (isHexColor(linkColor)) baseStyle.color = linkColor;
        else if (linkColor) baseClass += ` ${linkColor}`;
        if (!isHexColor(navLinkHoverColor) && navLinkHoverColor)
          baseClass += ` ${navLinkHoverColor}`;
        baseClass += isHexColor(navLinkHoverColor) || !navLinkHoverColor ? ' hover:opacity-80' : '';
        return {
          style: baseStyle,
          className: baseClass,
          isButton: false,
          linkColor,
          linkHover: navLinkHoverColor,
        };
    }
  };

  const sortedChildren = (link: NavigationMenuLink) =>
    [...(link.children || [])].sort((a, b) => a.order - b.order);

  const renderLogo = () => {
    const logo = data.logo;
    const siteName = data.siteName;
    if (!logo && siteName) {
      const brandColor = data.customColors?.brandText || navTheme.brandText || navTheme.text;
      const brandStyle: React.CSSProperties = isHexColor(brandColor) ? { color: brandColor } : {};
      const brandClass = isHexColor(brandColor)
        ? 'text-xl font-bold transition-colors'
        : `text-xl font-bold ${brandColor} transition-colors`;
      return (
        <Link
          to={getNavigationUrl('/')}
          className={brandClass}
          style={{ ...brandStyle, ...bodyFontStyle }}
        >
          {siteName}
        </Link>
      );
    }
    if (!logo) return null;

    const LogoWrapper = logo.linkUrl ? 'a' : 'div';
    const logoProps = logo.linkUrl
      ? {
          href: logo.linkUrl,
          target: logo.linkTarget || '_self',
          className: 'flex items-center hover:opacity-80 transition-opacity',
        }
      : { className: 'flex items-center' };

    const textSizeClasses = {
      small: 'text-lg',
      medium: 'text-xl',
      large: 'text-2xl',
      xl: 'text-3xl',
    };
    const textWeightClasses = { normal: 'font-normal', bold: 'font-bold', light: 'font-light' };
    const logoTextColor =
      data.customColors?.brandText ||
      logo.textColor ||
      navTheme.brandText ||
      navTheme.text ||
      '#000000';
    const logoTextStyle: React.CSSProperties = isHexColor(logoTextColor)
      ? { color: logoTextColor }
      : {};
    const legacyBoth = isLegacyBothLogo(logo);
    const showImage =
      (logo.mode === 'image' || legacyBoth) && (logo.imageUrl || logo.cloudfrontUrl);
    const showText = (logo.mode === 'text' || legacyBoth) && logo.text;
    const imageSizeMode = legacyBoth ? 'legacy-both' : 'image';
    const textSizeKey = legacyBoth ? 'medium' : logo.textSize || 'medium';
    const legacyTextExtras = legacyBoth ? ' truncate max-w-[180px] !text-base' : '';
    const textClass = isHexColor(logoTextColor)
      ? `${textSizeClasses[textSizeKey]} ${textWeightClasses[logo.textWeight || 'bold']} hover:opacity-80 transition-opacity${legacyTextExtras}`
      : `${textSizeClasses[textSizeKey]} ${textWeightClasses[logo.textWeight || 'bold']} ${logoTextColor} hover:opacity-80 transition-opacity${legacyTextExtras}`;

    return (
      <LogoWrapper {...logoProps}>
        <div
          className={`flex items-center min-w-0 max-h-14 overflow-hidden${legacyBoth ? ' flex-row gap-2' : ''}`}
        >
          {showImage &&
            (() => {
              const { width, height, maxWidth, maxHeight } = clampLogoImageSizeForRender(
                logo.imageWidth,
                logo.imageHeight,
                imageSizeMode
              );
              return (
                <img
                  src={logo.cloudfrontUrl || logo.imageUrl}
                  alt={logo.imageAlt || 'Logo'}
                  className="object-contain flex-shrink-0"
                  style={{
                    width: `${width}px`,
                    height: `${height}px`,
                    maxWidth: `${maxWidth}px`,
                    maxHeight: `${maxHeight}px`,
                  }}
                />
              );
            })()}
          {showText && (
            <span className={textClass} style={{ ...logoTextStyle, ...bodyFontStyle }}>
              {logo.text}
            </span>
          )}
        </div>
      </LogoWrapper>
    );
  };

  const dropdownPanelClass = [
    'min-w-[12rem] rounded-md py-1 shadow-lg',
    !isHexColor(dropdownBackground) ? dropdownBackground : '',
  ]
    .filter(Boolean)
    .join(' ');
  const dropdownPanelStyle: React.CSSProperties = isHexColor(dropdownBackground)
    ? { backgroundColor: dropdownBackground }
    : {};
  const childTextClass = !isHexColor(dropdownText) ? dropdownText || '' : '';
  const childTextStyle: React.CSSProperties = isHexColor(dropdownText)
    ? { color: dropdownText }
    : {};
  const childHoverClass = !isHexColor(dropdownHover) ? dropdownHover : '';

  const renderChildLinks = (link: NavigationMenuLink, includeParent: boolean) => {
    const children = sortedChildren(link);
    const rows = includeParent
      ? [
          {
            id: `${link.id || link.text}-self`,
            text: visibleMenuLinkText(link.text, link.url),
            url: link.url,
            target: link.target,
          },
          ...children.map(child => ({
            ...child,
            text: visibleMenuLinkText(child.text, child.url),
          })),
        ]
      : children.map(child => ({
          ...child,
          text: visibleMenuLinkText(child.text, child.url),
        }));
    return rows.map(child => {
      const resolvedUrl = resolveNavbarLinkUrl(
        { text: child.text, url: child.url, style: 'normal' },
        sortedLinks
      );
      return (
        <ButtonLink
          key={child.id || child.url}
          url={getNavigationUrl(resolvedUrl)}
          target={child.target}
          className={`nav-menu-child block px-4 py-2 text-sm ${childTextClass} ${childHoverClass}`.trim()}
          style={{ ...childTextStyle, ...bodyFontStyle, textDecoration: 'none' }}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          {child.text}
        </ButtonLink>
      );
    });
  };

  const toggleParent = (key: string) => {
    setOpenParents(current => ({ ...current, [key]: !current[key] }));
  };

  return (
    <>
      {(isHexColor(navLinkHoverColor) || isHexColor(dropdownHover)) && (
        <style>{`
          ${
            isHexColor(navLinkHoverColor)
              ? `[data-widget="navigation-menu"] .nav-hover-link:hover { color: ${navLinkHoverColor} !important; opacity: 1 !important; text-decoration: none !important; }`
              : ''
          }
          ${
            isHexColor(dropdownHover)
              ? `[data-widget="navigation-menu"] .nav-menu-child:hover { color: ${dropdownHover} !important; }`
              : ''
          }
        `}</style>
      )}
      <header className={navbarClasses.join(' ')} style={navbarStyle} data-widget="navigation-menu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <div className="flex-shrink-0 flex items-center max-h-16 min-w-0">{renderLogo()}</div>
            <div
              className={`hidden md:block ${linkAlignmentClasses[data.alignment || 'space-between']}`}
            >
              <div className="flex items-baseline space-x-4 gap-2">
                {sortedLinks.map((link, index) => {
                  const children = sortedChildren(link);
                  const linkStyleConfig = getLinkStyles(link.style || 'normal', false);
                  const resolvedUrl = resolveNavbarLinkUrl(link, sortedLinks);
                  const navUrl = getNavigationUrl(resolvedUrl);
                  if (children.length === 0) {
                    return (
                      <ButtonLink
                        key={link.id || index}
                        url={navUrl}
                        target={link.target}
                        className={linkStyleConfig.className}
                        style={{ ...linkStyleConfig.style, ...bodyFontStyle }}
                      >
                        {visibleMenuLinkText(link.text, link.url)}
                      </ButtonLink>
                    );
                  }
                  return (
                    <div key={link.id || index} className="relative group/menu">
                      <ButtonLink
                        url={navUrl}
                        target={link.target}
                        className={linkStyleConfig.className}
                        style={{ ...linkStyleConfig.style, ...bodyFontStyle }}
                      >
                        {visibleMenuLinkText(link.text, link.url)}
                        <ChevronDown className="h-4 w-4" aria-hidden="true" />
                      </ButtonLink>
                      <div className="absolute left-0 top-full z-50 hidden pt-1 group-hover/menu:block group-focus-within/menu:block">
                        <div className={dropdownPanelClass} style={dropdownPanelStyle} role="menu">
                          {renderChildLinks(link, false)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="md:hidden ml-auto">
              <button
                type="button"
                data-nav-menu-toggle=""
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md hover:opacity-80 transition-opacity"
                style={
                  isHexColor(data.customColors?.linkText || navTheme.linkText || navTheme.text)
                    ? { color: data.customColors?.linkText || navTheme.linkText || navTheme.text }
                    : {}
                }
                aria-expanded={isMobileMenuOpen ? 'true' : 'false'}
              >
                <span className="sr-only">
                  {isMobileMenuOpen ? 'Close main menu' : 'Open main menu'}
                </span>
                {isMobileMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
          <div className={`md:hidden ${isMobileMenuOpen ? '' : 'hidden'}`} data-nav-menu-mobile="">
            <div
              className="px-2 pt-2 pb-3 space-y-2 sm:px-3"
              style={isHexColor(backgroundColor) ? { backgroundColor } : {}}
            >
              {sortedLinks.map((link, index) => {
                const children = sortedChildren(link);
                const parentKey = link.id || String(index);
                const linkStyleConfig = getLinkStyles(link.style || 'normal', true);
                const resolvedUrl = resolveNavbarLinkUrl(link, sortedLinks);
                if (children.length === 0) {
                  return (
                    <ButtonLink
                      key={parentKey}
                      url={getNavigationUrl(resolvedUrl)}
                      target={link.target}
                      className={linkStyleConfig.className}
                      style={{ ...linkStyleConfig.style, ...bodyFontStyle }}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {visibleMenuLinkText(link.text, link.url)}
                    </ButtonLink>
                  );
                }
                const isOpen = !!openParents[parentKey];
                return (
                  <div key={parentKey}>
                    <button
                      type="button"
                      data-nav-menu-parent=""
                      className={linkStyleConfig.className}
                      style={{ ...linkStyleConfig.style, ...bodyFontStyle }}
                      aria-expanded={isOpen ? 'true' : 'false'}
                      onClick={() => toggleParent(parentKey)}
                    >
                      <span>{visibleMenuLinkText(link.text, link.url)}</span>
                      <ChevronDown
                        className={`h-4 w-4 ${isOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      />
                    </button>
                    <div
                      className={isOpen ? 'pl-3 pt-1 space-y-1' : 'hidden pl-3 pt-1 space-y-1'}
                      data-nav-menu-children=""
                    >
                      {renderChildLinks(link, true)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
